import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { DefaultRegion, IRegion } from 'src/Definitions';
import { Client, IRegionDto, RegionDto, SwaggerResponse } from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';

const ColumnSettingsDto = 'RegionDto';

export default class RegionsStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isModalOpen: boolean;
    @observable
    public regions: IRegion[];
    @observable
    public selectedRegion?: IRegion;
    public dataTableStore: DataTableStore<IRegionDto, IRegion>;

    constructor(private globalStore: GlobalStore, private client: Client) {
        this.dataTableStore = new DataTableStore<IRegionDto, IRegion>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllRegions(filter, page, undefined, orderBy, includeInactives),
            (region) => this.setupRegions(region),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public closeDialog() {
        this.isModalOpen = false;
        this.selectedRegion = undefined;
    }

    @action.bound
    public disableRegion(region: IRegion) {
        this.isLoading = true;
        return this.client
            .archiveRegion(region.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getColumnSettingsAndRegions() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user!.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.dataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    @action.bound
    public getAllRegions(pageNumber?: number, pageSize?: number) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        this.isLoading = true;
        return this.client
            .getAllRegions(undefined, pageNumber, pageSize)
            .then((resp: SwaggerResponse<IRegionDto[]>) =>
                runInAction(() => {
                    this.regions = this.setupRegions(resp.result);
                })
            );
    }

    @action.bound
    public openDialogWithRegion(region: IRegion) {
        this.isModalOpen = true;
        this.selectedRegion = region;
    }

    @action.bound
    public recoverRegion(region: IRegion) {
        this.isLoading = true;
        return this.client
            .reactivateRegion(region.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    private resetUIState(shouldClearFilter: boolean) {
        if (shouldClearFilter) {
            this.dataTableStore.clearFilter();
        }
        this.dataTableStore.loadData();
        this.closeDialog();
    }

    @action.bound
    public saveRegion(region: IRegion) {
        this.isLoading = true;
        let promise;
        const regionDto = new RegionDto(region);
        if (region.isNew) {
            regionDto.isActive = true;
            promise = this.client.createRegion(regionDto);
        } else {
            promise = this.client.updateRegion(region.id, regionDto);
        }
        return promise
            .then(() => {
                this.resetUIState(region.isNew);
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    private setupRegions = (regions: IRegionDto[]) => {
        return regions.map((x) => this.setupRegionMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (region: IRegionDto): IRegion => {
        return { ...DefaultRegion(), ...region };
    };

    private setupRegionMenuItems = (region: IRegion) => {
        region.displayChampions = region.champions.map((u) => u.name).join(', ');
        region.menuItems = [];
        region.menuItems.push({
            name: 'Edit',
            onClick: () => this.openDialogWithRegion(region),
        });
        if (region.isActive) {
            region.menuItems.push({
                isConfirm: true,
                name: 'Disable',
                onClick: () => this.disableRegion(region),
            });
        } else {
            region.menuItems.push({
                isConfirm: true,
                name: 'Recover',
                onClick: () => this.recoverRegion(region),
            });
        }
        return region;
    };
}



// WEBPACK FOOTER //
// ./src/stores/RegionsStore/RegionsStore.ts